import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {DialogStore} from '@ariakit/react'
import {FormProvider, useForm} from 'react-hook-form'
import {DeleteAgendaEventFormValidationSchema} from '../../formSchemas'
import {zodResolver} from '@hookform/resolvers/zod'
import type {AgendaEventAbsence} from '../../types'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useTranslation} from '@/translations/i18n'
import {Text} from '@/components/ui/text/Text'
import {format} from 'date-fns'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {absenceReasonToLabel} from '@/features/users/utils'
import {errorHandler} from '@/utilities/helpers'
import {useDeleteAgendaEvent} from '../../queries/useDeleteAgendaEvent'
import {useGetEvent} from '@/features/agenda/queries/useGetEvent'
import {toast} from '@/utilities/toast/toast'
import {DialogHeading} from '@/components/ui/dialog-atoms/DialogAtoms'

export const DeleteConsultantAbsenceDialog = ({
    event,
    dialogStore
}: {
    event: AgendaEventAbsence
    dialogStore: DialogStore
}) => {
    const {t} = useTranslation()
    const form = useForm<DeleteAgendaEventFormValidationSchema>({
        resolver: zodResolver(DeleteAgendaEventFormValidationSchema),
        defaultValues: {
            notificationsRecipients: event.eventConsultants?.map(consultant => ({
                id: consultant.userId,
                fullName: consultant.user.fullName
            }))
        }
    })

    // The result of this query will be also grabbed from the cache in the useSocketAgendaEvents hook
    // So it is needed to track the event object from the socket hook
    const {data: eventData, isLoading: isEventLoading} = useGetEvent({
        eventId: event.id,
        responseType: 'extended',
        options: {
            enabled: !!event.id
        }
    })

    const deleteEventMutation = useDeleteAgendaEvent({
        urlParams: {eventId: event.id},
        options: {
            onSuccess: () => {
                toast.success(t('agenda:consultant_absence_deleted'))
                dialogStore.hide()
            },
            onError: errorHandler
        }
    })

    const onSubmit = form.handleSubmit(data => {
        deleteEventMutation.mutate({
            notificationRecipientIds: data.notificationsRecipients?.map(recipient => recipient.id),
            notificationDetails: data.notificationDetails
        })
    })

    const consultant = eventData?.eventConsultants?.[0]

    return (
        <FormProvider {...form}>
            <ConfirmationDialog
                dialogStore={dialogStore}
                onConfirm={onSubmit}
                isConfirmPending={deleteEventMutation.isPending || isEventLoading}
                confirmButtonVariant="primaryDanger"
                confirmButtonText={t('commons:delete')}
                headerSlot={
                    <Flexbox direction="column" gap={7} fullWidth>
                        <Flexbox direction="column" fullWidth gap={1}>
                            <DialogHeading>{t('agenda:delete_consultant_absence_dialog_title')}</DialogHeading>
                            <Text palette="neutral" shade="500" size="textSm">
                                {`${consultant?.user.fullName} ${consultant?.user.specialization ? `(${consultant?.user.specialization})` : ''}`}
                            </Text>
                        </Flexbox>
                        <Grid columns={4} columnGap={6} rowGap={2} fullWidth>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('agenda:confirmation_start_date')}
                            </Text>
                            <Text palette="neutral" shade="900" size="textSm" weight="500">
                                {event.start && format(event.start, 'dd/MM/yyyy')}
                            </Text>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('agenda:confirmation_start_time')}
                            </Text>
                            <Text palette="neutral" shade="900" size="textSm" weight="500">
                                {event.start && format(event.start, 'HH:mm')}
                            </Text>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('agenda:confirmation_end_date')}
                            </Text>
                            <Text palette="neutral" shade="900" size="textSm" weight="500">
                                {event.end && format(event.end, 'dd/MM/yyyy')}
                            </Text>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('agenda:confirmation_end_time')}
                            </Text>
                            <Text palette="neutral" shade="900" size="textSm" weight="500">
                                {event.end && format(event.end, 'HH:mm')}
                            </Text>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('agenda:reason')}
                            </Text>
                            <GridItem spanColumn={3}>
                                <Text palette="neutral" shade="900" size="textSm" weight="500">
                                    {event.absenceMotivation ? t(absenceReasonToLabel[event.absenceMotivation]) : '-'}
                                </Text>
                            </GridItem>
                        </Grid>
                    </Flexbox>
                }
            />
        </FormProvider>
    )
}
